// src/App.tsx - VERSIÓN CORREGIDA (BUSCADOR PÚBLICO) 🛡️

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';

// ✅ IMPORTAR EL GUARDIA DE SEGURIDAD
import ProtectedRoute from './components/ProtectedRoute';

// Componentes de configuración profesional
import ProfessionalProfileConfig from './components/ProfessionalProfileConfig';
import MySubscription from './components/MySubscription';
import ProfessionalPhotoManager from './components/ProfessionalPhotoManager';
import ProfessionalNotificationConfig from './components/ProfessionalNotificationConfig';
import ProfessionalBookingFieldsConfig from './components/ProfessionalBookingFieldsConfig';

// Componentes principales
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import FAQ from './components/FAQ';
import FeaturesSection from './components/FeaturesSection';
import Login from './components/Login';
import Register from './components/Register';
import ProfessionalPaymentConfig from './components/ProfessionalPaymentConfig';

import SessionExpiryAlert from './components/SessionExpiryAlert';
import WhatsAppButton from './components/WhatsAppButton';
import BarraProfesional from './components/BarraProfesional';
import PaymentVerificationPanel from './components/PaymentVerificationPanel';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

// Scroll to top on route change
import ScrollToTop from './components/ScrollToTop';

// Dashboards
import PatientDashboard from './components/PatientDashboard';
import ProfessionalDashboardRouter from './components/ProfessionalDashboardRouter';
import BeautyProfessionalDashboard from './components/BeautyProfessionalDashboard';
import HealthProfessionalDashboard from './components/HealthProfessionalDashboard';
import AssistantDashboard from './components/AssistantDashboard';
import AssistantRegister from './components/AssistantRegister';

// Componentes de belleza
import AdvancedCalendar from './components/AdvancedCalendar';
import ResponsiveCalendar from './components/ResponsiveCalendar';
import Clients from './components/Clients';
import BeautyPortfolio from './components/BeautyPortfolio';
import Reports from './components/Reports';

// Componentes de profesionales
import ProfessionalServicesConfig from './components/ProfessionalServicesConfig';
import ProfessionalScheduleConfig from './components/ProfessionalScheduleConfig';
import ProfessionalLocationsConfig from './components/ProfessionalLocationsConfig';
import NewAppointmentForm from './components/NewAppointmentForm';
import AdminPanel from './components/AdminPanel';
import SearchProfessionals from './components/SearchProfessionals';
import ProfessionalProfile from './components/ProfessionalProfile';
import PrescriptionManager from './components/PrescriptionManager';
import PrescriptionEnrollment from './components/PrescriptionEnrollment';
import PublicPrescriptionValidator from './components/PublicPrescriptionValidator';
import BookAppointment from './components/BookAppointment';
import PatientProfile from './components/PatientProfile';
import PatientMedicalHistory from './components/PatientMedicalHistory';
import UniversalPortfolio from './components/UniversalPortfolio';
import PublicProfessionalLanding from './components/PublicProfessionalLanding';
import PublicAppointmentAction from './components/PublicAppointmentAction';

import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailure from './components/PaymentFailure';
import PaymentPending from './components/PaymentPending';
import MPConnectionSuccess from './components/MPConnectionSuccess';
import MPConnectionError from './components/Mpconnectionerror';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionFailure from './components/SubscriptionFailure';
import SubscriptionPending from './components/SubscriptionPending';
import ProfessionalPaymentDashboard from './components/ProfessionalPaymentDashboard';

import ClinicalRecordsPage from './components/ClinicalRecords/ClinicalRecordsPage';
import ClinicalRecordForm from './components/ClinicalRecords/ClinicalRecordForm';
import ClinicalRecordViewer from './components/ClinicalRecords/ClinicalRecordViewer';
import ClinicalTemplateBuilder from './components/ClinicalRecords/ClinicalTemplateBuilder';

// CONFIGURACIÓN GOOGLE OAUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '952300354138-auake886qfgm324brlco9b89s1ru9hr2.apps.googleusercontent.com';
const ENABLE_GOOGLE_AUTH = process.env.REACT_APP_ENABLE_GOOGLE_AUTH !== 'false' && !!GOOGLE_CLIENT_ID;

const HomePage: React.FC = () => (
  <div><Hero /><FeaturesSection /></div>
);

const PageWithLayout: React.FC<{ children: React.ReactNode; showFooter?: boolean }> = ({ children, showFooter = false }) => (
  <div className="min-h-screen flex flex-col"><Header /><main className="flex-1">{children}</main>{showFooter && <Footer />}</div>
);

const PageWithHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex flex-col"><Header /><main className="flex-1">{children}</main></div>
);

const GoogleOAuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!ENABLE_GOOGLE_AUTH || !GOOGLE_CLIENT_ID) return <>{children}</>;
  try {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
  } catch (error) {
    return <>{children}</>;
  }
};

function App() {
  return (
    <GoogleOAuthWrapper>
      <Router>
        <ScrollToTop />
        <SessionExpiryAlert />
        <div className="App">
          <Routes>
            {/* =======================================================
                🌍 ZONA PÚBLICA (Accesible por todos)
               ======================================================= */}
            
            <Route path="/" element={<PageWithLayout showFooter={true}><HomePage /></PageWithLayout>} />
            <Route path="/ayuda" element={<PageWithHeader><FAQ /></PageWithHeader>} />
            
            {/* ✅ CORRECCIÓN: Rutas de búsqueda movidas aquí (Públicas) */}
            <Route path="/search" element={<PageWithHeader><SearchProfessionals /></PageWithHeader>} />
            <Route path="/search-professionals" element={<PageWithHeader><SearchProfessionals /></PageWithHeader>} />

            {/* Auth */}
            <Route path="/login" element={<PageWithHeader><Login /></PageWithHeader>} />
            <Route path="/register" element={<PageWithHeader><Register /></PageWithHeader>} />
            <Route path="/assistant/register" element={<AssistantRegister />} /> {/* Registro asistente es público */}
            <Route path="/forgot-password" element={<PageWithHeader><ForgotPassword /></PageWithHeader>} />
            <Route path="/reset-password" element={<PageWithHeader><ResetPassword /></PageWithHeader>} />

            {/* Perfil Público y Validación */}
            <Route path="/p/:id" element={<PublicProfessionalLanding />} />
            <Route path="/receta" element={<PublicPrescriptionValidator />} />
            <Route path="/receta/:cuir" element={<PublicPrescriptionValidator />} />

            {/* Mercado Pago Retornos (Públicos o semi-públicos) */}
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failure" element={<PaymentFailure />} />
            <Route path="/payment-pending" element={<PaymentPending />} />
            <Route path="/mp-connection-success" element={<MPConnectionSuccess />} />
            <Route path="/mp-connection-error" element={<MPConnectionError />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/failure" element={<SubscriptionFailure />} />
            <Route path="/subscription/pending" element={<SubscriptionPending />} />

            {/* Booking público - cualquier usuario puede agendar */}
            <Route path="/book-appointment/:id" element={<PageWithHeader><BookAppointment /></PageWithHeader>} />

            {/* Acciones de turno desde WhatsApp (confirmar/cancelar) */}
            <Route path="/turno/:token" element={<PublicAppointmentAction />} />

            {/* =======================================================
                🛡️ ZONA PACIENTES (Solo rol: 'patient')
               ======================================================= */}
            <Route element={<ProtectedRoute allowedRoles={['patient']} />}>
              <Route path="/dashboard" element={<PageWithHeader><PatientDashboard /></PageWithHeader>} />
              <Route path="/dashboard/paciente" element={<PageWithHeader><PatientDashboard /></PageWithHeader>} />
              <Route path="/profile" element={<PageWithHeader><PatientProfile /></PageWithHeader>} />
              <Route path="/medical-history" element={<PageWithHeader><PatientMedicalHistory /></PageWithHeader>} />
            </Route>


            {/* =======================================================
                🛡️ ZONA PROFESIONALES (Solo rol: 'professional')
               ======================================================= */}
            <Route element={<ProtectedRoute allowedRoles={['professional']} />}>
              <Route path="/dashboard/profesional/*" element={<PageWithHeader><ProfessionalDashboardRouter /></PageWithHeader>} />
              
              {/* Rutas de configuración y gestión */}
              <Route path="/professional/subscription" element={<PageWithHeader><MySubscription /></PageWithHeader>} />
              <Route path="/professional/profile-config" element={<PageWithHeader><ProfessionalProfileConfig /></PageWithHeader>} />
              <Route path="/professional/services-config" element={<PageWithHeader><ProfessionalServicesConfig /></PageWithHeader>} />
              <Route path="/professional/schedule-config" element={<PageWithHeader><ProfessionalScheduleConfig /></PageWithHeader>} />
              <Route path="/professional/locations-config" element={<PageWithHeader><ProfessionalLocationsConfig /></PageWithHeader>} />
              <Route path="/professional/payment-config" element={<PageWithHeader><ProfessionalPaymentConfig /></PageWithHeader>} />
              <Route path="/professional/notification-config" element={<PageWithHeader><ProfessionalNotificationConfig /></PageWithHeader>} />
              <Route path="/professional/booking-fields-config" element={<PageWithHeader><ProfessionalBookingFieldsConfig /></PageWithHeader>} />
              <Route path="/professional/portfolio" element={<PageWithHeader><UniversalPortfolio /></PageWithHeader>} />
              <Route path="/professional/payment-verification" element={<PageWithHeader><PaymentVerificationPanel /></PageWithHeader>} />
              <Route path="/professional/payment-dashboard" element={<PageWithHeader><ProfessionalPaymentDashboard /></PageWithHeader>} />
              <Route path="/professional/new-appointment" element={<PageWithHeader><NewAppointmentForm /></PageWithHeader>} />
              <Route path="/professional/reports" element={<PageWithHeader><Reports /></PageWithHeader>} />
              <Route path="/professional/clients" element={<PageWithHeader><Clients /></PageWithHeader>} />
              
              
              {/* Calendarios */}
              <Route path="/professional/calendar" element={<PageWithHeader><ResponsiveCalendar /></PageWithHeader>} />
              <Route path="/beauty/calendar" element={<PageWithHeader><AdvancedCalendar /></PageWithHeader>} />
              <Route path="/health/calendar" element={<PageWithHeader><AdvancedCalendar /></PageWithHeader>} />

              {/* Dashboards específicos */}
              <Route path="/beauty/dashboard" element={<PageWithHeader><BeautyProfessionalDashboard /></PageWithHeader>} />
              <Route path="/health/dashboard" element={<HealthProfessionalDashboard />} />
              <Route path="/professional/patients" element={<HealthProfessionalDashboard />} />
              <Route path="/beauty/portfolio" element={<PageWithHeader><BeautyPortfolio /></PageWithHeader>} />

              {/* Historias Clínicas y Recetas */}
              <Route path="/professional/prescriptions" element={<PageWithHeader><PrescriptionManager /></PageWithHeader>} />
              <Route path="/professional/habilitar-recetas" element={<PageWithHeader><PrescriptionEnrollment /></PageWithHeader>} />
              <Route path="/professional/clinical-records" element={<PageWithHeader><ClinicalRecordsPage /></PageWithHeader>} />
              <Route path="/professional/clinical-records/new" element={<PageWithHeader><ClinicalRecordForm /></PageWithHeader>} />
              <Route path="/professional/clinical-records/:recordId" element={<PageWithHeader><ClinicalRecordViewer /></PageWithHeader>} />
              <Route path="/professional/clinical-records/:recordId/edit" element={<PageWithHeader><ClinicalRecordForm /></PageWithHeader>} />
              <Route path="/professional/clinical-records/template/new" element={<ClinicalTemplateBuilder />} />
              <Route path="/professional/clinical-records/template/:templateId/edit" element={<ClinicalTemplateBuilder />} />
              
              {/* Rutas legacy o en desarrollo */}
              <Route path="/health/patients" element={<PageWithHeader><div className="p-8 text-center">Pacientes - En desarrollo</div></PageWithHeader>} />
              <Route path="/health/reports" element={<PageWithHeader><div className="p-8 text-center">Reportes Médicos - En desarrollo</div></PageWithHeader>} />
            </Route>


            {/* =======================================================
                🛡️ ZONA ASISTENTES (Solo rol: 'assistant')
               ======================================================= */}
            <Route element={<ProtectedRoute allowedRoles={['assistant']} />}>
              <Route path="/assistant/dashboard" element={<PageWithHeader><AssistantDashboard /></PageWithHeader>} />
              <Route path="/assistant/calendar" element={<PageWithHeader><ResponsiveCalendar /></PageWithHeader>} />
              <Route path="/assistant/payment-verification" element={<PageWithHeader><PaymentVerificationPanel /></PageWithHeader>} />
              {/* Horarios de una de sus profesionales: ver y cambiar, queda registrado quién */}
              <Route path="/assistant/horarios/:professionalId" element={<PageWithHeader><ProfessionalScheduleConfig /></PageWithHeader>} />
            </Route>


            {/* =======================================================
                🛠️ ZONA ADMIN (Opcional, protegida)
               ======================================================= */}
            <Route path="/admin" element={<AdminPanel />} />


            {/* =======================================================
                ❌ 404 NOT FOUND
               ======================================================= */}
            <Route path="*" element={
              <PageWithHeader>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                    <p className="text-gray-600 mb-8">Página no encontrada</p>
                    <a href="/" className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors">Volver al inicio</a>
                  </div>
                </div>
              </PageWithHeader>
            } />

          </Routes>
          
          <WhatsAppButton />
          <BarraProfesional />
        </div>
      </Router>
    </GoogleOAuthWrapper>
  );
}

export default App;